import { useMutation, useQuery, keepPreviousData} from '@tanstack/react-query';
import useAlert from 'hooks/useAlert';
import { useRouter } from 'next/router';
import { ReactNode, createContext, useCallback, useEffect, useState } from 'react';
import { ResponsePage } from 'types/common/response';
import { Search } from 'types/common/search';
import { ReturnModel, TableActionRowsObj, TableContextProps, TableTemplateProp } from 'types/common/table';
import { handleInitialValues } from 'utils/form';
import { getCurrentOrgScope } from 'utils/orgScope';

const initSearch: Search<any, any> = { page: 1, rows: 10, total_rows: 0, filter: {}, datas: [] };
const initResponse: ResponsePage<any> = {
  code: 200,
  message: '',
  success: false,
  meta: { total_rows: 0 },
  payload: []
};

type TableProviderProps = {
  children: ReactNode;
} & TableTemplateProp;

const initialState: TableContextProps = {
  search: initSearch,
  response: initResponse,
  isSearchLoading: false,
  handleRefresh: () => {},
  handlePage: () => {},
  handlePageSize: () => {},
  handleSearch: () => {},
  handleFilterChange: () => {}
};

const TableContext = createContext(initialState);

const isSafePathSegment = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const segment = String(value).trim();
  if (!segment) return false;
  return !/^(undefined|null)$/i.test(segment);
};

const normalizeBasePath = (path?: string): string => {
  if (!path) return '';
  const normalized = path.trim();
  if (!normalized) return '';
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const buildSwitchPayload = (row: any) => {
  if (typeof row?.active === 'boolean') {
    return { active: !row.active };
  }

  if (typeof row?.status === 'boolean') {
    return { status: !row.status };
  }

  if (row?.active_status === 'active' || row?.active_status === 'inactive') {
    return { active_status: row.active_status === 'active' ? 'inactive' : 'active' };
  }

  if (row?.status === 'active' || row?.status === 'inactive') {
    return { status: row.status === 'active' ? 'inactive' : 'active' };
  }

  return null;
};

function TableConfigProvider({
  type,
  key_column,
  base_path,
  name,
  api,
  children,
  call_back,
  action_rows,
  action_tops,
  filterUpdate,
  model,
  isAlert
}: TableProviderProps) {
  const [search, setSearch] = useState<Search<any, any>>(initSearch);
  const [masters, setMasters] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSwitchModal, setOpenSwitchModal] = useState(false);
  const [switchRow, setSwitchRow] = useState<any>(null);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [text, setText] = useState('');
  const [form, setForm] = useState({});
  const router = useRouter();
  const { alertError, alertSuccess } = useAlert();

  const safePath = normalizeBasePath(base_path);
  const orgScope = getCurrentOrgScope();
  const { data, refetch, isFetching: isSearchLoading } = useQuery({ queryKey: [`search_${name}`, safePath, orgScope, search], queryFn: () => api.fetchSearch(search), placeholderData: keepPreviousData,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false });

  const { data: mastersData } = useQuery({ queryKey: [`init_${name}`, safePath, orgScope], queryFn: () => (api.fetchInit ? api.fetchInit() : new Promise((r, e) => r([]))), placeholderData: keepPreviousData,
      refetchOnWindowFocus: false });

  const exportMute = useMutation({ mutationFn: (filter: any) => api.fetchExport && api.fetchExport(filter) });
  const exportImgMute = useMutation({ mutationFn: (filter: any) => api.fetchExportImage && api.fetchExportImage(filter) });
  const deleteMute = useMutation({ mutationFn: (row: any) => api.deleteFunc && api.deleteFunc(row) });
  const saveMute = useMutation({ mutationFn: (row: any) => api.patchUpdate && api.patchUpdate(row) });
  const downloadMute = useMutation({ mutationFn: (row: any) => api.downloadFunc && api.downloadFunc(row) });
  const copyMute = useMutation({ mutationFn: (row: any) => api.copyFunc && api.copyFunc(row) });

  useEffect(() => {
    setMasters(mastersData);
  }, [mastersData]);

  useEffect(() => {
    if (filterUpdate) {
      setSearch((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          ...(typeof filterUpdate === 'object' && filterUpdate !== null && !Array.isArray(filterUpdate)
            ? filterUpdate
            : {})
        }
      }));
    }
  }, [filterUpdate]);

  const handlePage = (_e: unknown, page: number) => {
    setSearch((prev) => ({ ...prev, page }));
  };

  const handlePageSize = (rows: number) => {
    setSearch((prev) => ({ ...prev, rows }));
  };

  /** page ที่สองจากฟอร์มค้นหา (เช่น TableForm ส่ง 1) — ถ้าไม่ส่งจะรีเซ็ตไปหน้า 1 */
  const handleFilterChange = (filter: any, page?: number) => {
    setSearch((prev) => ({
      ...prev,
      page: page === undefined ? 1 : page,
      filter
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleSearch = useCallback((search: Search<any, any>) => {
    setSearch(search);
  }, []);

  const handleExport = useCallback(
    (filter: any) => {
      alertSuccess('กำลังสร้างไฟล์');
      exportMute.mutateAsync({ filter });
    },
    [alertSuccess, exportMute]
  );

  const handleExportImage = useCallback(
    (filter: any) => {
      alertSuccess('กำลังสร้างไฟล์');
      exportImgMute.mutateAsync({ filter });
    },
    [alertSuccess, exportImgMute]
  );

  const handleBtnImport = useCallback(() => {
    const btn = action_tops?.find((p) => p.type === 'import');
    setText(btn?.label ?? 'นำเข้าข้อมูล');

    if (call_back?.callBackNew) {
      call_back.callBackNew(true);
      return;
    }

    handleRefresh();
    setOpenImportModal(true);
  }, [action_tops, call_back, handleRefresh]);

  const handleImport = useCallback(async (result: any) => {
    try {
      if (result.submit && result.data) {
        // const copyResult: any =
        // await copyMute.mutateAsync(result.data);
        setOpenImportModal(false);
        refetch();
        // alertSuccess(copyResult?.message || 'คัดลอกข้อมูลสำเร็จ');
      } else {
        setOpenImportModal(false);
      }
    } catch (error: any) {
      alertError(error?.response?.data?.message || 'คัดลอกข้อมูลไม่สำเร็จ');
    }
  }, []);

  const handleNew = useCallback(() => {
    const btn = action_tops?.find((p) => p.type === 'add');
    if (btn) {
      setText(btn.label ?? 'เพิ่มข้อมูล');
    } else {
      setText('เพิ่มข้อมูล');
    }
    if (type == 'page') {
      if (name === 'ShiftWork') {
        setOpenModal(true);
      }
      call_back?.callBackNew && call_back?.callBackNew(true);
    } else if (type === 'model' || type === 'drawer') {
      setOpenModal(true);
      setForm(handleInitialValues(model?.inputs || []));
      call_back?.callBackNew && call_back?.callBackNew(true);
    } else {
      call_back?.callBackNew && call_back?.callBackNew();
    }
  }, []);

  const handleUpdate = useCallback(async (row: any) => {
    const btn = action_rows?.find((p) => (p as TableActionRowsObj).type === 'edit');
    if (btn) {
      setText((btn as TableActionRowsObj).text || 'แก้ไขข้อมูล' + (row?.name ? row.name : ''));
    } else {
      setText('แก้ไขข้อมูล' + (row?.name ? row.name : ''));
    }
    if (call_back?.callBackUpdate) {
      call_back?.callBackUpdate(row);
      if (type === 'drawer') {
        setForm(row);
        setOpenModal(true);
      }
    } else if (type === 'model' || type === 'drawer') {
      if (api.fetchOneData) {
        const data = await api.fetchOneData(row);
        setForm(data);
        setOpenModal(true);
      } else {
        setForm(row);
        setOpenModal(true);
      }
    } else {
      const idValue = row?.[key_column ?? 'id'];
      const safeBasePath = normalizeBasePath(base_path);

      if (!safeBasePath || !isSafePathSegment(idValue)) {
        alertError('ไม่สามารถเปิดหน้ารายละเอียดได้ เนื่องจากข้อมูลไม่สมบูรณ์');
        return;
      }

      router.push(`${safeBasePath}/${String(idValue).trim()}`);
    }
  }, []);

  const handleBtnDelete = useCallback((row: any) => {
    const action = action_rows?.find((p) => (p as TableActionRowsObj).type === 'delete');
    if (call_back?.callBackDelete) {
      call_back?.callBackDelete(row);
      if (type === 'drawer') {
        setForm(row);
        setOpenModal(true);
      }
    } else {
      if (action) {
        const subText = (action as TableActionRowsObj).text
          ? (action as TableActionRowsObj).text + ' ' + row[(action as TableActionRowsObj).use_name]
          : row[(action as TableActionRowsObj).use_name];
        setText(subText);
      } else {
        setText('ลบรายการ');
      }
      setOpenDeleteModal(true);
      setForm(row);
    }
  }, []);

  const handleDelete = useCallback(async (result: any) => {
    try {
      if (result.submit && result.data) {
        const deleteResult: any = await deleteMute.mutateAsync(result.data);
        setOpenDeleteModal(false);
        refetch();
        alertSuccess(deleteResult?.message || 'บันทึกข้อมูลสำเร็จ');
      } else {
        setOpenDeleteModal(false);
      }
    } catch (error: any) {
      alertError(error?.response?.data?.message || 'ลบข้อมูลไม่สำเร็จ');
    }
  }, []);

  const handleSwitch = useCallback(
    (row: any) => {
      if (call_back?.callBackSwitch) {
        call_back.callBackSwitch(row);
        return;
      }

      if (!api.patchUpdate) {
        alertError('ไม่สามารถสลับสถานะได้ เนื่องจากไม่ได้ตั้งค่าการทำงานไว้');
        return;
      }

      setSwitchRow(row);
      setForm(row);
      setOpenSwitchModal(true);
    },
    [alertError, api.patchUpdate, call_back]
  );

  const handleSwitchConfirm = useCallback(
    async (result: any) => {
      try {
        if (result.submit && switchRow) {
          const id = switchRow?.[key_column ?? 'id'];
          const data = buildSwitchPayload(switchRow);

          if (!isSafePathSegment(id) || !data) {
            alertError('ไม่สามารถสลับสถานะได้ เนื่องจากข้อมูลไม่สมบูรณ์');
            setOpenSwitchModal(false);
            setSwitchRow(null);
            return;
          }

          const saveResult: any = await saveMute.mutateAsync({ id, data });
          if (isAlert !== false) {
            alertSuccess(saveResult?.message || 'บันทึกข้อมูลสำเร็จ');
          }
          refetch();
        }
        setOpenSwitchModal(false);
        setSwitchRow(null);
      } catch (error: any) {
        alertError(error?.response?.data?.message || 'สลับสถานะไม่สำเร็จ');
        setOpenSwitchModal(false);
        setSwitchRow(null);
      }
    },
    [alertError, alertSuccess, isAlert, key_column, refetch, saveMute, switchRow]
  );

  const handleBtnCopy = useCallback((row: any) => {
    const action = action_rows?.find((p) => (p as TableActionRowsObj).type === 'copy');
    if (action) {
      const subText = (action as TableActionRowsObj).text
        ? (action as TableActionRowsObj).text + ' ' + row[(action as TableActionRowsObj).use_name]
        : row[(action as TableActionRowsObj).use_name];
      setText(subText);
    } else {
      setText('คัดลอกรายการ');
    }
    setOpenCopyModal(true);
    setForm(row);
  }, []);

  const handleCopy = useCallback(async (result: any) => {
    try {
      if (result.submit && result.data) {
        await copyMute.mutateAsync(result.data);
        setOpenCopyModal(false);
      } else {
        setOpenCopyModal(false);
      }
    } catch (error: any) {
      alertError(error?.response?.data?.message || 'คัดลอกข้อมูลไม่สำเร็จ');
    }
  }, []);

  const handleDownload = useCallback((row: any) => {
    downloadMute.mutate(row);
  }, []);

  const handleSubmitModal = useCallback(
    async ({ id, data, submit }: ReturnModel) => {
      try {
        if (submit) {
          const submitResult: any = await saveMute.mutateAsync({ id, data });
          setOpenModal(false);
          if (isAlert !== false) {
            alertSuccess(submitResult?.message || 'บันทึกข้อมูลสำเร็จ');
          }
          refetch();
        } else {
          setOpenModal(false);
        }
      } catch (error: any) {
        alertError(error?.response?.data?.message || 'บันทึกข้อมูลไม่สำเร็จ');
      }
    },
    [alertError, alertSuccess, isAlert, refetch, saveMute]
  );

  const handleView = useCallback((row: ReturnModel) => {
    if (call_back?.callBackView) {
      call_back?.callBackView(row);
    }
  }, []);

  const handleSort = useCallback((key_column: string) => {
    setSearch((prev) => {
      const f = (prev.filter || {}) as Record<string, unknown>;
      const same = key_column === f.sort_type;
      const sort_order = same ? (f.sort_order === 'asc' ? 'desc' : 'asc') : 'asc';
      return {
        ...prev,
        filter: {
          ...prev.filter,
          sort_type: key_column,
          sort_order
        }
      };
    });
  }, []);

  const value: TableContextProps = {
    search,
    form,
    masters,
    openModal,
    text,
    handleRefresh,
    openDeleteModal,
    openSwitchModal,
    openCopyModal,
    openImportModal,
    response: data,
    isSearchLoading,
    handlePage,
    handlePageSize,
    handleFilterChange,
    handleSearch,
    handleNew,
    handleExport,
    handleImport,
    handleUpdate,
    handleSwitch,
    handleSwitchConfirm,
    handleDelete,
    handleBtnDelete,
    handleDownload,
    handleSubmitModal,
    handleView,
    handleCopy,
    handleBtnCopy,
    handleBtnImport,
    handleExportImage: api.fetchExportImage ? handleExportImage : undefined,
    handleSort
  };
  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}

export { TableConfigProvider, TableContext };
